
import { supabase } from '@/integrations/supabase/client';
import { jwtDecode } from 'jwt-decode';

// Debug helper - only logs in development
const debugSession = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[SessionService] ${message}`, data || '');
  }
};

/**
 * Refreshes the current session if possible
 */
export const refreshSession = async (): Promise<boolean> => {
  try {
    debugSession('Attempting to refresh session...');
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      debugSession('Error refreshing session:', error);
      return false;
    }
    
    if (!data.session) {
      debugSession('No session returned after refresh');
      return false;
    }
    
    // Check if the refreshed token is valid
    if (isTokenExpired(data.session.access_token)) {
      debugSession('Refreshed token is still expired');
      return false;
    }
    
    // Update last activity timestamp
    updateLastActivityTimestamp();
    debugSession('Session refreshed successfully');
    return true;
  } catch (error) {
    debugSession('Exception in refreshSession:', error);
    return false;
  }
};

/**
 * Gets the current session asynchronously
 */
export const getCurrentSession = async () => {
  return await supabase.auth.getSession();
};

/**
 * Checks if the JWT token is expired by decoding it
 * @param token JWT token to check
 * @returns boolean indicating if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Add a small buffer (10 seconds) to avoid edge cases
    const isExpired = decoded.exp < currentTime + 10;
    
    debugSession('Token expiration check', { 
      expires: new Date(decoded.exp * 1000).toISOString(),
      now: new Date(currentTime * 1000).toISOString(),
      isExpired
    });
    
    return isExpired;
  } catch (error) {
    debugSession('Error decoding JWT token:', error);
    // If we can't decode the token, consider it expired
    return true;
  }
};

/**
 * Checks if there is a valid session with explicit JWT expiration check
 */
export const hasValidSession = async (): Promise<boolean> => {
  try {
    debugSession('Checking session validity');
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      debugSession('Error checking session validity:', error);
      return false;
    }
    
    // Check if we have a session
    if (!data.session) {
      debugSession('No session found');
      return false;
    }
    
    // Explicitly check token expiration
    const accessToken = data.session.access_token;
    if (isTokenExpired(accessToken)) {
      debugSession('JWT token is expired, attempting refresh');
      
      // Try to refresh the token
      const refreshed = await refreshSession();
      if (!refreshed) {
        debugSession('Token refresh failed, clearing session data');
        clearSessionData();
        return false;
      }
      
      debugSession('Token refreshed successfully');
      return true;
    }
    
    // If we reach here, the session is valid
    // Update the last activity timestamp
    updateLastActivityTimestamp();
    debugSession('Session is valid');
    return true;
  } catch (error) {
    debugSession('Exception in hasValidSession:', error);
    return false;
  }
};

/**
 * Clears session data from localStorage
 * Use before login to ensure fresh state
 */
export const clearSessionData = (): void => {
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivityTimestamp');
    localStorage.removeItem('sessionExpiryTime');
    localStorage.removeItem('redirectAfterLogin');
    debugSession('Session data cleared');
  } catch (e) {
    debugSession('Error clearing session data:', e);
  }
};

/**
 * Updates the last activity timestamp to prevent session timeout
 * Call this on user interactions to keep the session alive
 */
export const updateLastActivityTimestamp = (): void => {
  try {
    const timestamp = Date.now().toString();
    localStorage.setItem('lastActivityTimestamp', timestamp);
    debugSession('Activity timestamp updated', new Date(parseInt(timestamp)).toISOString());
  } catch (e) {
    debugSession('Error updating last activity timestamp:', e);
  }
};

/**
 * Check if session has timed out due to inactivity
 * @param timeoutMinutes Session timeout in minutes (default: 60)
 * @returns boolean indicating if session has timed out
 */
export const hasSessionTimedOut = (timeoutMinutes = 60): boolean => {
  try {
    const lastActivityTime = parseInt(localStorage.getItem('lastActivityTimestamp') || '0', 10);
    const currentTime = Date.now();
    const sessionTimeout = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds
    const timeSinceLastActivity = currentTime - lastActivityTime;
    
    debugSession('Checking session timeout', {
      lastActivity: new Date(lastActivityTime).toISOString(),
      current: new Date(currentTime).toISOString(),
      timeout: `${timeoutMinutes} minutes`,
      timeSinceLastActivity: `${Math.round(timeSinceLastActivity/1000/60)} minutes`
    });
    
    return timeSinceLastActivity > sessionTimeout;
  } catch (e) {
    debugSession('Error checking session timeout:', e);
    return false; // Default to not timed out on error
  }
};

// Store current path for redirect after login
export const storeRedirectPath = (path: string): void => {
  try {
    if (path !== '/login' && path !== '/signup') {
      localStorage.setItem('redirectAfterLogin', path);
      debugSession('Stored redirect path', path);
    }
  } catch (e) {
    debugSession('Error storing redirect path:', e);
  }
};

// Get redirect path after login
export const getRedirectPath = (): string => {
  try {
    const path = localStorage.getItem('redirectAfterLogin') || '/';
    debugSession('Retrieved redirect path', path);
    return path;
  } catch (e) {
    debugSession('Error getting redirect path:', e);
    return '/';
  }
};

// Legacy functions maintained for compatibility

/**
 * Validates the current session and returns user ID if valid
 * Added for backward compatibility
 */
export const validateSession = async (): Promise<{ validSession: boolean, userId: string | null, sessionError: any }> => {
  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      return { validSession: false, userId: null, sessionError: error };
    }
    
    if (!data.session) {
      return { validSession: false, userId: null, sessionError: null };
    }
    
    // Check token expiration explicitly
    const accessToken = data.session.access_token;
    if (isTokenExpired(accessToken)) {
      console.log('JWT token is expired in validateSession');
      return { validSession: false, userId: null, sessionError: new Error('Token expired') };
    }
    
    // Update the expiry time and last activity
    try {
      const expiryTime = new Date(data.session.expires_at || '').getTime();
      localStorage.setItem('sessionExpiryTime', expiryTime.toString());
      updateLastActivityTimestamp();
    } catch (e) {
      console.error('Error updating session timestamps:', e);
    }
    
    return { 
      validSession: true, 
      userId: data.session.user.id, 
      sessionError: null 
    };
  } catch (error) {
    console.error('Error validating session:', error);
    return { validSession: false, userId: null, sessionError: error };
  }
};

/**
 * Validates and normalizes a user ID
 * Added for backward compatibility
 */
export const validateUserId = (
  userId: string, 
  sessionUserId: string | null
): { userId: string, isAuthenticated: boolean, isValidId: boolean } => {
  // Check if the ID is valid UUID
  const isValidId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
  
  // Check if we have a valid session user ID that matches
  const isAuthenticated = !!sessionUserId && sessionUserId === userId;
  
  // Return the validated ID and status
  return {
    userId,
    isAuthenticated,
    isValidId
  };
};
